// src/context/BranchContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../utils/api";

const BranchContext = createContext();

const formatDate = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const BranchProvider = ({ children }) => {
  const [selectedBranch, setSelectedBranch] = useState("All");
  const [branchList, setBranchList] = useState([]);
  // machine_name (display name) → branch name
  const [machineToBranchMap, setMachineToBranchMap] = useState({});
  // meter_id / hierarchy node key → branch name
  const [meterIdToBranchMap, setMeterIdToBranchMap] = useState({});

  const fetchBranches = async () => {
    const username = localStorage.getItem("username");
    if (!username) {
      setBranchList([]);
      setMachineToBranchMap({});
      setMeterIdToBranchMap({});
      return;
    }

    try {
      const end = new Date();
      end.setDate(end.getDate() - 1);
      end.setHours(23, 59, 59, 999);
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      start.setHours(0, 0, 0, 0);

      const params = new URLSearchParams({
        username,
        mode: "hour",
        start: formatDate(start),
        end: formatDate(end),
        selected_machines: "Usage",
        metric: "power_kw",
      });

      const response = await api.get(`/machines_data?${params.toString()}`);
      const resData = response.data?.data || response.data;

      if (resData?.Hierarchy) {
        // 1. Collect all branch nodes
        const branches = Object.entries(resData.Hierarchy)
          .filter(([_, data]) => data && typeof data === "object" && data.is_branch)
          .map(([id, data]) => ({
            id,
            name: data.name || id,
            hierarchy_path: data.hierarchy_path || "",
          }));
        setBranchList(branches);

        // Sort longest path first so child-branches match before parent-branches
        const sortedBranches = [...branches].sort(
          (a, b) => b.hierarchy_path.length - a.hierarchy_path.length
        );

        const nameMap = {};   // machine_name → branch name
        const meterMap = {};  // meter_id / node key → branch name

        Object.entries(resData.Hierarchy).forEach(([nodeId, nodeData]) => {
          if (!nodeData || typeof nodeData !== "object") return;
          if (nodeData.is_branch) return; // skip the branch nodes themselves

          const nodePath = nodeData.hierarchy_path || "";
          const parentBranch = sortedBranches.find(
            (b) =>
              b.hierarchy_path &&
              nodePath.startsWith(b.hierarchy_path) &&
              nodePath !== b.hierarchy_path
          );
          if (!parentBranch) return;

          // Map by display name
          const nodeName = nodeData.name || nodeId;
          nameMap[nodeName] = parentBranch.name;

          // Map by hierarchy node key (often equals meter_id in /client-machines)
          meterMap[nodeId] = parentBranch.name;

          // Also map by the explicit meter_id field on the node, if present
          if (nodeData.meter_id) {
            meterMap[String(nodeData.meter_id)] = parentBranch.name;
          }
        });

        setMachineToBranchMap(nameMap);
        setMeterIdToBranchMap(meterMap);
      }
    } catch (error) {
      console.error("Failed to fetch branches in global context:", error);
    }
  };

  useEffect(() => {
    fetchBranches();
    // Retry periodically in case the username is not yet in localStorage on first render
    const interval = setInterval(fetchBranches, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <BranchContext.Provider
      value={{
        selectedBranch,
        setSelectedBranch,
        branchList,
        setBranchList,
        machineToBranchMap,
        meterIdToBranchMap,
      }}
    >
      {children}
    </BranchContext.Provider>
  );
};

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error("useBranch must be used within a BranchProvider");
  }
  return context;
};
